import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, ShoppingCart } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';
import { useLocalization } from '../../contexts/LocalizationContext';

const WishlistSidebar = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { wishlist, loading, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { formatPrice, getPrice } = useLocalization();
  
  const currentLang = i18n.language || 'en';
  const wishlistItems = wishlist?.items || [];

  const handleRemoveItem = async (productId) => {
    await removeFromWishlist(productId);
  };

  const handleAddToCart = async (item) => {
    const success = await addToCart(item.productId);
    if (success) {
      // Optionally remove from wishlist after adding to cart
      // await removeFromWishlist(item.productId);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:w-[400px] flex flex-col p-0">
        <SheetHeader className="border-b p-6">
          <SheetTitle className="text-xl font-semibold">
            {t('nav.wishlist')} ({wishlistItems.length})
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
          </div>
        ) : wishlistItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg
                className="w-16 h-16 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-900 mb-2">
              {t('wishlist.empty')}
            </p>
            <p className="text-sm text-gray-500 mb-4 text-center">
              {t('wishlistSidebar.emptyDescription')}
            </p>
            <Link to="/shop" onClick={onClose}>
              <Button className="mt-4 bg-[#be1f1a] hover:bg-[#a71b17] text-white">
                {t('cart.continueShopping')}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 p-6">
              <div className="space-y-4">
                {wishlistItems.map((item, index) => (
                  <div
                    key={`${item.productId}-${index}`}
                    className="flex gap-4 pb-4 border-b border-gray-200"
                  >
                    <Link to={`/product/${item.slug}`} onClick={onClose}>
                      <img
                        src={item.image?.thumbnail || item.image?.medium || 'https://placehold.co/80x100'}
                        alt={item.name?.[currentLang] || item.name?.en || t('wishlistSidebar.product')}
                        className="w-20 h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                      />
                    </Link>
                    <div className="flex-1">
                      <Link to={`/product/${item.slug}`} onClick={onClose}>
                        <h4 className="font-medium text-sm mb-1 hover:text-[#be1f1a] transition-colors">
                          {item.name?.[currentLang] || item.name?.en || t('wishlistSidebar.product')}
                        </h4>
                      </Link>
                      <p className="text-sm font-semibold text-[#be1f1a] mb-2">
                        {formatPrice(item.price || 0)}
                      </p>
                      <Button
                        size="sm"
                        onClick={() => handleAddToCart(item)}
                        className="bg-[#be1f1a] hover:bg-[#a71b17] text-white h-8 text-xs"
                      >
                        <ShoppingCart className="h-3 w-3 mr-1" />
                        {t('products.addToCart')}
                      </Button>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item.productId)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      aria-label={t('wishlistSidebar.removeFromWishlist')}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t p-6">
              <Link to="/shop" onClick={onClose}>
                <Button
                  variant="outline"
                  className="w-full"
                >
                  {t('cart.continueShopping')}
                </Button>
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default WishlistSidebar;
